import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useOutletContext } from 'react-router';
import styled from 'styled-components';

import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandXXS, LabelM } from '@sorare/core/src/atoms/typography';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import { Stadium } from '@sorare/core/src/components/animations/Stadium';
import { Dialog } from '@sorare/core/src/components/dialog';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK_REWARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { So5Lineup } from 'components/lineup/So5Lineup';

import type {
  FixtureCompetitionTrckGrpTrckRewardsContenderPageQuery,
  FixtureCompetitionTrckGrpTrckRewardsContenderPageQueryVariables,
} from './__generated__/index.graphql';

const Header = styled(SBHorizontal)`
  padding: var(--double-unit);
  z-index: 1;
`;

const LineupWrapper = styled.div`
  max-width: 720px;
  width: 100%;
  margin: auto auto;
`;

const StadiumWrapper = styled.div`
  position: relative;
  inset: 0;
  width: 100%;
  padding: 0 calc(var(--unit) * 8);
`;

const Sup = styled(BrandXXS).attrs({
  as: 'sup',
})`
  display: inline-block;
  color: var(--c-nd-600);
`;

const StyledBrandL = styled(BrandL)`
  &.rank-1 {
    --text-gradient-color: var(--c-gradient-gold);
  }
  &.rank-2 {
    --text-gradient-color: var(--c-gradient-silver);
  }
  &.rank-3 {
    --text-gradient-color: var(--c-gradient-bronze);
  }
`;

const FIXTURE_REWARDS_TRCKGRP_CONTENDER_PAGE_QUERY = gql`
  query FixtureCompetitionTrckGrpTrckRewardsContenderPageQuery(
    $contender: String!
  ) {
    so5 {
      so5LeaderboardContender(slug: $contender) {
        slug
        so5Lineup {
          id
          so5Rankings {
            id
            score
            ranking
          }
          user {
            slug
            id
            ...Avatar_publicUserInfoInterface
            ...UserName_publicUserInfoInterface
            ...MyClubLink_publicUserInfoInterface
          }
        }
        ...So5Lineup_so5LeaderboardContender
      }
    }
  }
  ${So5Lineup.fragments.so5LeaderboardContender}
  ${Avatar.fragments.publicUserInfoInterface}
  ${UserName.fragments.user}
  ${MyClubLink.fragments.user}
` as TypedDocumentNode<
  FixtureCompetitionTrckGrpTrckRewardsContenderPageQuery,
  FixtureCompetitionTrckGrpTrckRewardsContenderPageQueryVariables
>;

export const loaderFn = <
  Params extends {
    contender: string;
  },
>() =>
  queryLoader<Params>(
    FIXTURE_REWARDS_TRCKGRP_CONTENDER_PAGE_QUERY,
    ({ contender }) => ({
      contender,
    })
  );

type Props = {
  fixture: string;
  competition: string;
  eventType?: string;
  trckGrp: string;
  trck: string;
};
export const FixtureCompetitionTrckGrpTrckRewardsContenderPage = ({
  fixture,
  competition,
  eventType,
  trckGrp,
  trck,
}: Props) => {
  const { data } =
    useQueryLoader<FixtureCompetitionTrckGrpTrckRewardsContenderPageQuery>();
  const navigate = useNavigate();
  const { generateSportContextPath } = useSportContext();
  const { fieldUrl } = useOutletContext<{ fieldUrl: string }>();
  const isDesktopAndAbove = useIsDesktopAndAbove();

  const { so5LeaderboardContender } = data.so5 || {};

  if (!so5LeaderboardContender?.so5Lineup)
    return (
      <StadiumWrapper>
        <Stadium fieldUrl={fieldUrl} completion={1} />
      </StadiumWrapper>
    );

  const { so5Lineup } = so5LeaderboardContender;

  const { so5Rankings } = so5Lineup;

  const { ranking, score } = so5Rankings[0] || {};

  const onClose = () => {
    navigate(
      generateSportContextPath(
        ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK_REWARDS,
        {
          params: {
            fixture,
            eventType: eventType || '',
            competition,
            trckGrp,
            trck,
          },
        }
      )
    );
  };

  return (
    <ConditionalWrapper
      Wrapper={Dialog}
      wrap={!isDesktopAndAbove}
      props={{ open: true, onClose }}
    >
      <Vertical grow>
        <Header>
          <MyClubLink user={so5Lineup.user}>
            <Horizontal>
              <Avatar size={6} rounded user={so5Lineup.user} />
              <Vertical gap={0.5}>
                <LabelM color="var(--c-nd-600)">
                  <UserName user={so5Lineup.user} />
                </LabelM>
                <StyledBrandL
                  className={classNames(`rank-${ranking}`, {
                    'text-gradient-color': ranking && ranking < 4,
                  })}
                >
                  <Ordinal ordinal={ranking ?? 0} Sup={Sup} />
                </StyledBrandL>
              </Vertical>
            </Horizontal>
          </MyClubLink>
          <BrandL>
            <FormattedMessage
              {...fantasy.ordinalPoints}
              values={{ score, sup: c => <Sup>{c}</Sup> }}
            />
          </BrandL>
        </Header>
        <LineupWrapper>
          <StadiumWrapper>
            <Stadium fieldUrl={fieldUrl} completion={1} />

            <So5Lineup
              so5LeaderboardContender={so5LeaderboardContender}
              lineupStyle="spread"
            />
          </StadiumWrapper>
        </LineupWrapper>
      </Vertical>
    </ConditionalWrapper>
  );
};
